/**
 * ============================================================
 * KEJJA OS — src/pages/calendar/CalendarLayout.tsx
 * Indigo accent. 3 views: Month, Week, Agenda.
 * Shares: date cursor, visible calendars, routines overlay toggle.
 * ============================================================
 */

import { useState } from 'react';
import { NavLink, Outlet, useOutletContext } from 'react-router-dom';
import {
  CalendarDays, CalendarRange, CalendarClock, Plus,
  Repeat, Check,
} from 'lucide-react';
import { useMyHomes } from '@/hooks/useMe';
import { useCalendars } from '@/hooks/useCalendar';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { EventForm } from './EventForm';

export interface CalendarContext {
  homeId: string;
  currentUserId: string | undefined;
  homeName: string;

  // Date cursor (lifted so all views share it)
  cursorDate: Date;
  setCursorDate: (d: Date) => void;

  // Visible calendar toggles
  visibleCalendarIds: Set<string>;
  toggleCalendar: (id: string) => void;

  // Routines overlay
  routinesOn: boolean;
  setRoutinesOn: (v: boolean) => void;

  // Create event panel
  openNewEvent: (hint?: { date?: string; allDay?: boolean }) => void;
}

export function useCalendarContext(): CalendarContext {
  return useOutletContext<CalendarContext>();
}

const TABS = [
  { to: '/calendar',         label: 'Month',  icon: CalendarDays,  end: true },
  { to: '/calendar/week',    label: 'Week',   icon: CalendarRange },
  { to: '/calendar/agenda',  label: 'Agenda', icon: CalendarClock },
];

export default function CalendarLayout() {
  const { data: homesData, isLoading: homesLoading } = useMyHomes();
  const first = homesData?.homes?.[0];
  const homeId = first?.home.id;
  const homeName = first?.home.name ?? '';
  const currentUserId = undefined;   // wire to /me when needed

  const [cursorDate, setCursorDate] = useState(() => {
    const d = new Date();
    d.setUTCHours(0, 0, 0, 0);
    return d;
  });

  const [routinesOn, setRoutinesOn] = useState(true);
  const [visibleIds, setVisibleIds] = useState<Set<string>>(new Set());
  const [allCalendarsInitialized, setAllCalendarsInitialized] = useState(false);

  const [showEventForm, setShowEventForm] = useState(false);
  const [eventFormHint, setEventFormHint] = useState<{ date?: string; allDay?: boolean } | null>(null);
  const [editingEventId, setEditingEventId] = useState<string | null>(null);

  const { data: calendarsData, isLoading: calendarsLoading } = useCalendars(homeId);

  // On first load of calendars, show all by default
  if (calendarsData && !allCalendarsInitialized) {
    const allIds = new Set(calendarsData.calendars.map((c) => c.id));
    setVisibleIds(allIds);
    setAllCalendarsInitialized(true);
  }

  const toggleCalendar = (id: string) => {
    const next = new Set(visibleIds);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setVisibleIds(next);
  };

  const openNewEvent = (hint?: { date?: string; allDay?: boolean }) => {
    setEditingEventId(null);
    setEventFormHint(hint ?? null);
    setShowEventForm(true);
  };

  const openEditEvent = (eventId: string) => {
    setEditingEventId(eventId);
    setEventFormHint(null);
    setShowEventForm(true);
  };

  if (homesLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-10 w-64" />
        <Skeleton className="h-10 w-full" />
        <Skeleton className="h-64 w-full" />
      </div>
    );
  }

  if (!homeId) {
    return <div className="text-sm text-muted-foreground">No home found. Create a home first.</div>;
  }

  const ctx: CalendarContext = {
    homeId, currentUserId, homeName,
    cursorDate, setCursorDate,
    visibleCalendarIds: visibleIds,
    toggleCalendar,
    routinesOn, setRoutinesOn,
    openNewEvent,
  };

  const calendars = calendarsData?.calendars ?? [];

  return (
    <div className="space-y-5">
      <header className="border-b border-border pb-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <div className="h-10 w-10 rounded-xl bg-indigo-100 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300 flex items-center justify-center flex-shrink-0">
              <CalendarDays className="w-5 h-5" />
            </div>
            <div className="min-w-0">
              <h1 className="text-xl md:text-2xl font-bold text-foreground leading-tight tracking-tight">
                Calendar
              </h1>
              <p className="text-xs text-muted-foreground mt-0.5 truncate">
                <span className="italic">Every date that matters.</span>
                {homeName && <span className="ml-2">· {homeName}</span>}
              </p>
            </div>
          </div>
          <Button
            size="sm"
            className="bg-indigo-600 hover:bg-indigo-700 text-white flex-shrink-0"
            onClick={() => openNewEvent()}
          >
            <Plus className="w-4 h-4 mr-1" /> New
          </Button>
        </div>
      </header>

      {/* Tabs */}
      <nav className="flex items-center gap-1 overflow-x-auto">
        {TABS.map((tab) => (
          <NavLink
            key={tab.to}
            to={tab.to}
            end={tab.end}
            className={({ isActive }) => cn(
              'flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium whitespace-nowrap transition-colors',
              isActive
                ? 'bg-indigo-100 dark:bg-indigo-950/40 text-indigo-700 dark:text-indigo-300'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground',
            )}
          >
            <tab.icon className="w-3.5 h-3.5" />
            {tab.label}
          </NavLink>
        ))}
      </nav>

      {/* Calendar filter chips + routines toggle */}
      {!calendarsLoading && calendars.length > 0 && (
        <div className="flex items-center gap-1.5 flex-wrap">
          {calendars.map((c) => {
            const on = visibleIds.has(c.id);
            return (
              <button
                key={c.id}
                onClick={() => toggleCalendar(c.id)}
                className={cn(
                  'flex items-center gap-1.5 pl-2 pr-2.5 py-1 rounded-full border text-[11px] font-medium transition-all',
                  on
                    ? 'border-transparent'
                    : 'border-border bg-background/50 text-muted-foreground opacity-60',
                )}
                style={on ? {
                  backgroundColor: `${c.color}22`,
                  color: c.color,
                  borderColor: `${c.color}66`,
                } : undefined}
              >
                {on && <Check className="w-2.5 h-2.5" />}
                {c.icon && <span>{c.icon}</span>}
                <span className="truncate max-w-[120px]">{c.name}</span>
                {c.ownerUserId === null && (
                  <span className="text-[9px] opacity-70">shared</span>
                )}
              </button>
            );
          })}
          <button
            onClick={() => setRoutinesOn(!routinesOn)}
            className={cn(
              'flex items-center gap-1.5 pl-2 pr-2.5 py-1 rounded-full border text-[11px] font-medium transition-all',
              routinesOn
                ? 'border-teal-500/60 bg-teal-500/10 text-teal-700 dark:text-teal-300'
                : 'border-border bg-background/50 text-muted-foreground opacity-60',
            )}
          >
            {routinesOn && <Check className="w-2.5 h-2.5" />}
            <Repeat className="w-2.5 h-2.5" />
            <span>Routines</span>
          </button>
        </div>
      )}

      <Outlet context={ctx} />

      {showEventForm && (
        <EventForm
          homeId={homeId}
          eventId={editingEventId}
          hint={eventFormHint}
          onClose={() => { setShowEventForm(false); setEditingEventId(null); setEventFormHint(null); }}
          onEditRequest={openEditEvent}
        />
      )}
    </div>
  );
}
